import React, { useState } from "react";
import EditableTextField from "../Input/EditableTextField";
import CustomIconButton from "../Buttons/CustomIconButton"; // Import CustomIconButton
import BusinessHoursEditor from "../business_hours/BusinessHoursEditor";

import { HiExclamation } from "react-icons/hi";

import ConfirmationModal from "../ModalComponents/ConfirmationModal";
import SavePopupMessage from "../ModalComponents/SavePopupMessage";

import FastAPIClient from "../../client";
import config from "../../config";
import { HiOutlinePencil, HiChevronDown, HiChevronUp } from "react-icons/hi2";

import EditableDatePicker from "../Input/EditableDatePicker";
import EditableSelectField from "../Input/EditableSelectField";
import { ClientBottomInterface } from "../../types/client_bottom";

const client = new FastAPIClient(config);

const WarningIcon = () => (
    <div
        className="flex flex-row items-center justify-center"
        style={{ color: "#FF6A13" }}
    >
        <HiExclamation className="w-4 h-4 " />
    </div>
);

const ClientBottom: React.FC<ClientBottomInterface> = ({
    client_id,
    client_name_full,
    is_seasonal,
    season_start,
    season_end,
    address_1,
    phone,
    mail,
    website_url,
    menu_url,
    client_level_1,
    zip_code,
    city,
    interior_settings,
    has_terrace,
    terrace_settings,
    business_hours,
    cuisine,
    purchasing_manager,
}) => {
    // State to manage the editing mode
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const [initialFormData, setInitialFormData] = useState({
        client_id,
        client_name_full,
        address_1,
        phone,
        mail,
        website_url,
        menu_url,
        client_level_1,
        zip_code,
        city,
        interior_settings,
        terrace_settings,
        business_hours,
        is_seasonal,
        season_start,
        season_end,
        cuisine,
        purchasing_manager,
    });

    const [formData, setFormData] = useState(initialFormData);

    // State for popup message
    const [popupMessage, setPopupMessage] = useState<string | null>(null);
    const [popupType, setPopupType] = useState<"success" | "error" | null>(
        null
    );

    // Handle field value change
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value === "true" ? true : value === "false" ? false : value,
        }));
    };

    const handleDateChange = (name: string, date: string) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: date,
        }));
    };

    // Toggle editing mode
    const toggleEditingMode = () => {
        setIsEditing((prev) => {
            if (!prev) {
                setInitialFormData(formData); // Store the original values before editing
            }
            return !prev;
        });
    };

    const [isSaving, setIsSaving] = useState(false);

    // Handle save functionality (send data to backend)
    const handleSave = async () => {
        setIsSaving(true); // Start loading

        if (formData.business_hours) {
            const updatedBusinessHours = { ...formData.business_hours };

            for (const day in updatedBusinessHours) {
                const { open_times, is_closed, is_24_hours } =
                    updatedBusinessHours[day];

                if (is_closed || is_24_hours) {
                    updatedBusinessHours[day].open_times = [
                        { open_time: null, close_time: null },
                    ];
                } else {
                    if (!Array.isArray(open_times)) {
                        console.error(
                            `Error: open_times is not an array for day ${day}`
                        );
                        continue;
                    }

                    const isValid = open_times.some((time) => {
                        if (
                            !time.open_time?.trim() ||
                            !time.close_time?.trim()
                        ) {
                            return false;
                        }

                        if (time.open_time.includes("--")) {
                            console.error(
                                `Invalid open_time for day ${day}: ${time.open_time}`
                            );
                            setPopupMessage(
                                "Les heures d'ouverture ne peuvent pas contenir '--'."
                            );
                            setPopupType("error");

                            setTimeout(() => {
                                setPopupMessage(null);
                            }, 3000);

                            setIsSaving(false); // Stop loading if validation fails
                            return false;
                        }

                        return true;
                    });

                    if (!isValid) {
                        console.error(
                            `Validation failed for day ${day}: No valid open and close times found.`
                        );
                        setPopupMessage(
                            "Veuillez saisir au moins une heure d'ouverture et de fermeture pour chaque jour ouvert."
                        );
                        setPopupType("error");

                        setTimeout(() => {
                            setPopupMessage(null);
                        }, 3000);

                        setIsSaving(false); // Stop loading if validation fails
                        return;
                    }
                }
            }

            setFormData((prev) => ({
                ...prev,
                business_hours: updatedBusinessHours,
            }));
        }

        try {
            const response = await client.updateClient(formData);

            if (response.status === 200 || response.status === 201) {
                setPopupMessage("Les modifications ont été enregistrées.");
                setPopupType("success");
                setIsEditing(false);
            } else {
                throw new Error("Failed to update client");
            }
        } catch (error) {
            console.error("API error:", error);
            setPopupMessage(
                "Une erreur est survenue lors de l'enregistrement."
            );
            setPopupType("error");
        }

        setTimeout(() => {
            setPopupMessage(null);
        }, 3000);

        setIsSaving(false); // Stop loading after API response
    };

    const handleCancel = () => {
        setFormData(initialFormData); // Reset formData to original values
        setIsEditing(false); // Exit edit mode
    };

    const [expandedGroups, setExpandedGroups] = useState<{
        [key: string]: boolean;
    }>({});

    const toggleGroup = (groupName: string) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupName]: !prev[groupName],
        }));
    };

    const groups = [
        {
            title: "Générale",
            fields: [
                { label: "Nom Enseigne", name: "client_name_full" },
                { label: "Adresse", name: "address_1" },
                { label: "Code Postal", name: "zip_code" },
                { label: "Ville", name: "city" },
            ],
        },
        {
            title: "Contact",
            fields: [
                { label: "Responsable d'achat", name: "purchasing_manager" },
                { label: "Téléphone", name: "phone" },
                { label: "Email", name: "mail" },
            ],
        },
        {
            title: "Typologie",
            fields: [
                { label: "Typologie client", name: "client_level_1" },
                { label: "Cuisine", name: "cuisine" },
            ],
        },
        {
            title: "Capacité",
            fields: [
                { label: "# Places Intérieur", name: "interior_settings" },
                { label: "# Places Extérieur", name: "terrace_settings" },
            ],
        },
        {
            title: "Saisonnalité",
            fields: [
                {
                    label: "Saisonnier",
                    name: "is_seasonal",
                    type: "select",
                    options: [
                        { value: "true", label: "Oui" },
                        { value: "false", label: "Non" },
                    ],
                },
                ...(formData.is_seasonal
                    ? [
                          {
                              label: "Début de Saison",
                              name: "season_start",
                              type: "date",
                          },
                          {
                              label: "Fin de Saison",
                              name: "season_end",
                              type: "date",
                          },
                      ]
                    : []),
            ],
        },
        {
            title: "Horaires",
            fields: [
                {
                    label: "Horaires d'ouverture",
                    name: "business_hours",
                    type: "business_hours",
                },
            ],
        },
    ];

    const isGroupValid = (fields: { name: string }[]) => {
        return fields.some(({ name }) => {
            // If the field is null, undefined, or an empty string, it's invalid
            if (!formData[name]) return true;

            // Special handling for business_hours
            if (name === "business_hours") {
                // If business_hours is an empty object, it's invalid
                if (Object.keys(formData.business_hours || {}).length === 0) {
                    return true;
                }

                // If any open_time contains "--", it's invalid
                return Object.values(formData.business_hours || {}).some(
                    (day) =>
                        day.open_times.some((time) =>
                            time.open_time?.includes("--")
                        )
                );
            }

            return false;
        });
    };

    return (
        <div className="text-left text-gray-700 text-md mt-5 md:mt-0 mb-20 md:mb-0">
            <div className="flex flex-row w-full items-center">
                <div className="font-semibold text-black text-lg">
                    Informations client
                </div>
                <div className="ml-10 text-sm">
                    {!isEditing && (
                        <CustomIconButton
                            onClick={toggleEditingMode}
                            icon={<HiOutlinePencil />}
                            sx={{ fontSize: "18px" }}
                        />
                    )}
                </div>
            </div>

            {/* Editable fields */}

            <div className="space-y-1">
                {groups.map(({ title, fields }) => (
                    <div key={title} className="border-b py-2">
                        {/* Group Title & Expand/Collapse Button */}
                        <div
                            className="flex justify-between items-center cursor-pointer py-1 rounded-md text-black"
                            onClick={() => toggleGroup(title)}
                        >
                            <span className="flex font-semibold text-base w-2/3">
                                {title}
                            </span>
                            <span className="flex font-semibold text-base w-1/6">
                                {isGroupValid(fields) &&
                                    !expandedGroups[title] && <WarningIcon />}
                            </span>
                            <span className="flex w-1/6 items-end text-lg font-bold">
                                {expandedGroups[title] ? (
                                    <HiChevronUp />
                                ) : (
                                    <HiChevronDown />
                                )}
                            </span>
                        </div>

                        {/* Group Content - Display Fields Only If Expanded */}
                        {expandedGroups[title] && (
                            <div className="mt-2 space-y-1">
                                {fields.map(
                                    ({ label, name, type, options }) => (
                                        <div
                                            key={name}
                                            className="flex flex-row md:items-center space-y-1 md:space-y-0"
                                        >
                                            {type === "select" ? (
                                                <EditableSelectField
                                                    name={name}
                                                    value={
                                                        formData[name] === true
                                                            ? "true"
                                                            : formData[name] ===
                                                              false
                                                            ? "false"
                                                            : ""
                                                    }
                                                    isEditing={isEditing}
                                                    options={options || []}
                                                    onChange={handleChange}
                                                    label={label}
                                                />
                                            ) : type === "date" ? (
                                                <EditableDatePicker
                                                    value={formData[name] || ""}
                                                    isEditing={isEditing}
                                                    onChange={(date) =>
                                                        handleDateChange(
                                                            name,
                                                            date
                                                        )
                                                    }
                                                    label={label}
                                                />
                                            ) : type === "business_hours" ? (
                                                <BusinessHoursEditor
                                                    businessHours={
                                                        formData.business_hours
                                                    }
                                                    isEditing={isEditing}
                                                    onSave={(updatedHours) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            business_hours:
                                                                updatedHours,
                                                        }))
                                                    }
                                                />
                                            ) : (
                                                <EditableTextField
                                                    name={name}
                                                    value={formData[name] || ""}
                                                    isEditing={isEditing}
                                                    onChange={handleChange}
                                                    label={label}
                                                    error={false}
                                                    helperText={""}
                                                />
                                            )}

                                            {(formData[name] === null ||
                                                formData[name] === undefined ||
                                                formData[name] === "") && (
                                                <WarningIcon />
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Button to save the changes */}
            {isEditing && (
                <ConfirmationModal
                    onCancel={handleCancel}
                    onSave={handleSave}
                    isEditing={true}
                    isSaving={isSaving}
                    message="Voulez-vous enregistrer les modifications ?"
                />
            )}

            {has_terrace && <div>{has_terrace}</div>}

            {/* Display the popup message */}
            {popupMessage && (
                <SavePopupMessage
                    popupType={popupType}
                    popupMessage={popupMessage}
                />
            )}
        </div>
    );
};

export default ClientBottom;
